import React, {
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import Helmet from 'react-helmet';
import { Global, css } from '@emotion/core';
import Layout from '../../shared/components/Layout/Layout';
import { CalendarModalHandler, SecondLayerModal } from './CalendarModalHandler';
import {
  fetchAllExercisesForAccount,
  fetchAthletes,
  fetchTagsForAccount,
  fetchQuestionsForAccount,
  fetchParentCalendars,
  fetchSavedWorkouts,
  fetchAndSetDefaultCalendar,
  fetchAndSetUrlAthlete,
  setSelectedDate,
} from './ducks/calendarActions';
import CalendarLayout from './CalendarLayout';
import EntryPointContent from './components/presentational/EntryPointContent';

const Calendar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const accountCode = currentUser?.accountCode;
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );

  const [hasHandledUrlParams, setHasHandledUrlParams] = useState(false);
  const viewType = useSelector((state) => state.calendar.data.viewType);

  // Parse URL for athlete ID and date (e.g., /calendar/A5000088/2025-10-16)
  useEffect(() => {
    if (!accountCode || hasHandledUrlParams) return;

    const pathParts = location.pathname.split('/').filter(Boolean);
    // Check if URL matches pattern: /calendar/A{userId}/{date}
    if (pathParts[0] === 'calendar' && pathParts[1] && pathParts[1].startsWith('A') && pathParts[2]) {
      const athleteIdWithA = pathParts[1]; // e.g., "A5000088"
      const dateString = pathParts[2]; // e.g., "2025-10-16"

      // Remove the 'A' prefix to get the userId
      const userId = athleteIdWithA.substring(1);

      // Validate that we have a numeric userId and a valid date
      if (/^\d+$/.test(userId) && moment(dateString, 'YYYY-MM-DD', true).isValid()) {
        // Set the date
        const dateMoment = moment(dateString);
        dispatch(setSelectedDate(dateMoment));

        // Fetch and set the athlete
        dispatch(fetchAndSetUrlAthlete(accountCode, parseInt(userId, 10)));

        // Mark that we've handled the URL params to prevent re-running
        setHasHandledUrlParams(true);
      }
    }
  }, [accountCode, location.pathname, hasHandledUrlParams]);

  useEffect(() => {
    /** fetch all exercises associated with all types for side bar */
    if (accountCode) {
      batch(() => {
        dispatch(fetchAllExercisesForAccount(accountCode));
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchTagsForAccount(accountCode));
        dispatch(fetchQuestionsForAccount(accountCode));
        dispatch(fetchParentCalendars(accountCode));
        dispatch(fetchSavedWorkouts(accountCode));
      });
    }
  }, [accountCode]);

  useEffect(() => {
    // Check if URL contains athlete ID pattern to prevent loading default calendar
    const pathParts = location.pathname.split('/').filter(Boolean);
    const hasAthleteInUrl = pathParts[0] === 'calendar' && pathParts[1] && pathParts[1].startsWith('A');

    if (currentUser && Object.keys(currentUser).length > 0 && currentUser.defaultCalendar) {
      // Only fetch default calendar if no calendar is currently selected AND no athlete in URL
      // This prevents overriding a newly created calendar or URL-specified athlete
      if (!hasAthleteInUrl && (!selectedReduxCalendar || Object.keys(selectedReduxCalendar).length === 0) && (!selectedReduxAthlete || Object.keys(selectedReduxAthlete).length === 0) && programType === 'calendar') {
        dispatch(fetchAndSetDefaultCalendar(currentUser));
      }
    }
  }, [currentUser, selectedReduxCalendar, location.pathname]);

  return (
    <Layout fullWidth overflowVisible={viewType === 'weekView'}>
      {viewType === 'weekView' && (
      <Global
        styles={css`
        body, html {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        html, body, #app, #app > div {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
                }
        `}
      />
      )}
      <Helmet>
        <title>Calendar | TeamBuildr</title>
      </Helmet>
      {/* <SupersetOverlay /> */}
      <CalendarModalHandler />
      <SecondLayerModal />
      {!Object.keys(selectedReduxCalendar).length
  && !Object.keys(selectedReduxAthlete).length
  && !Object.keys(selectedReduxNonDateBasedProgram).length
        ? <EntryPointContent /> : <CalendarLayout />}
    </Layout>
  );
};

Calendar.propTypes = { };
export default Calendar;
